import { useEffect, useRef, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import AnimatedRoutes from "./AnimatedRoutes";
import Footer from "./components/Footer";
import Header from "./components/Header";
import LangSwitchOverlay from "./components/LangSwitchOverlay";
import Preloader from "./components/Preloader";
import { LangOverlayProvider, useLangOverlay } from "./context/LangOverlayContext";
import { LinkProvider } from "./LinkProvider";

function PasswordScreen({ onAccess }) {
  const [pass, setPass] = useState("");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = () => {
    if (!pass.trim() || loading) return;
    setLoading(true);
    setError(false);
    fetch("https://whoareyou.devindex.net/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pass }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.result === true && data.token) {
          const expires = new Date().getTime() + 60 * 60 * 1000;
          localStorage.setItem("authToken", JSON.stringify({ token: data.token, expires }));
          onAccess();
        } else {
          setError(true);
          setPass("");
          inputRef.current?.focus();
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  const onKey = (e) => {
    if (e.key === "Enter") submit();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#05193A]">
      <motion.div
        initial={{ scale: 0.88, opacity: 0, y: 16 }}
        animate={{ scale: 1, opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } }}
        className="flex flex-col items-center gap-[24px] rounded-[24px] bg-[#0c2d6b] px-[60px] py-[44px] text-center shadow-[0_32px_80px_rgba(0,0,0,0.45)] w-[360px]"
      >
        <p className="font-['Nunito_Sans'] text-[20px] font-black text-[#DAEBFC]">
          Enter password
        </p>

        <div className="flex flex-col gap-[10px] w-full">
          <input
            ref={inputRef}
            type="password"
            value={pass}
            onChange={(e) => { setPass(e.target.value); setError(false); }}
            onKeyDown={onKey}
            placeholder="Password"
            className={`w-full rounded-[10px] border bg-[#05193A] px-[16px] py-[10px] font-['Nunito_Sans'] text-[15px] text-white outline-none placeholder-white/30 transition-colors ${
              error ? "border-red-400" : "border-white/20 focus:border-white/50"
            }`}
          />
          {error && (
            <p className="font-['Nunito_Sans'] text-[13px] text-red-400">Incorrect password</p>
          )}
        </div>

        <button
          type="button"
          onClick={submit}
          disabled={loading}
          className="w-full rounded-[10px] bg-[#DAEBFC] py-[10px] font-['Nunito_Sans'] text-[15px] font-black text-[#05193A] transition-opacity hover:opacity-80 disabled:opacity-50"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-[8px]">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="block h-[6px] w-[6px] rounded-full bg-[#05193A]"
                  style={{ animation: `lang-bounce 1.2s ease-in-out ${i * 0.18}s infinite` }}
                />
              ))}
            </span>
          ) : (
            "Enter"
          )}
        </button>

        <style>{`
          @keyframes lang-bounce {
            0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
            40% { transform: translateY(-6px); opacity: 1; }
          }
        `}</style>
      </motion.div>
    </div>
  );
}

function AppInner() {
  const { activeLang } = useLangOverlay();
  const [preloading, setPreloading] = useState(true);
  const [access, setAccess] = useState(true);

  useEffect(() => {
    const authData = JSON.parse(localStorage.getItem("authToken"));
    if (authData && authData.expires > new Date().getTime()) {
      setAccess(true);
    } else {
      localStorage.removeItem("authToken");
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setPreloading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  if (!access) return <PasswordScreen onAccess={() => setAccess(true)} />;

  return (
    <div>
      <Preloader visible={preloading} />
      <LangSwitchOverlay lang={activeLang} />
      <Router>
        <Header />
        <AnimatedRoutes />
        <Footer />
      </Router>
    </div>
  );
}

function App() {
  return (
    <LinkProvider>
      <LangOverlayProvider>
        <AppInner />
      </LangOverlayProvider>
    </LinkProvider>
  );
}

export default App;
